import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import clsx from "clsx"

import { golive, type AuditRow, type Deployment, type DeploymentSummary, type Person, type Session } from "@/api/golive"
import { Avatar } from "@/components/Standup"
import { CompleteDialog, CreateDialog, ItemDialog, LoginDialog } from "@/components/golive/dialogs"
import type { GoLiveHeaderField, GoLivePhase, GoLiveStatus, GoLiveTask, StatusId } from "@/types/golive"

/* ---- header field controls: which keys render as pulldowns ---- */
const PATTERN_OPTS = [
  ["", "—"],
  ["A", "A — zero-downtime"],
  ["B", "B — maintenance window"],
] as const
// key → how to edit it. Leads take up to N people; comms takes 1.
const LEAD_MAX: Record<string, number> = { deploymentLead: 2, rollbackLead: 2, communicationLead: 1 }

/* --------------------------------------------------------------- atoms */

const TONE: Record<GoLiveStatus["tone"], string> = {
  neutral: "bg-slate-100 text-slate-600 ring-slate-200",
  info: "bg-blue-50 text-blue-700 ring-blue-200",
  error: "bg-red-50 text-red-700 ring-red-200",
  success: "bg-win-light text-win-deep ring-win/30",
  muted: "bg-slate-50 text-slate-400 ring-slate-200",
}
const STATUS_META: Record<StatusId, { label: string; tone: GoLiveStatus["tone"] }> = {
  "not-started": { label: "Not Started", tone: "neutral" },
  "in-progress": { label: "In Progress", tone: "info" },
  blocked: { label: "Blocked", tone: "error" },
  done: { label: "Done", tone: "success" },
  na: { label: "N/A", tone: "muted" },
}
const STATUS_IDS: StatusId[] = ["not-started", "in-progress", "blocked", "done", "na"]

function StatusChip({ status }: { status: StatusId }) {
  const m = STATUS_META[status]
  return (
    <span className={clsx("inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1", TONE[m.tone])}>
      {m.label}
    </span>
  )
}

function GateIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4">
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}
function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
      <path d="M13 2 4 14h6l-1 8 9-12h-6z" />
    </svg>
  )
}

function TaskFlags({ task }: { task: GoLiveTask }) {
  return (
    <>
      {task.gate && (
        <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200">
          <GateIcon /> Gate
        </span>
      )}
      {task.critical && (
        <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-700 ring-1 ring-red-200">
          <BoltIcon /> Critical
        </span>
      )}
      {task.pattern && (
        <span className="rounded bg-headerSub/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-header ring-1 ring-headerSub/25">
          Pattern {task.pattern}
        </span>
      )}
      {task.oneTime && (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">
          one-time
        </span>
      )}
    </>
  )
}

function OwnerCell({ task, firstNameToFull }: { task: GoLiveTask; firstNameToFull: Map<string, string> }) {
  const parts = (task.owner || "")
    .split("/")
    .map((t) => t.trim())
    .filter(Boolean)
    .map((token) => ({ token, full: firstNameToFull.get(token.split(/\s+/)[0]?.toLowerCase()) ?? null }))
  return (
    <span className="flex flex-wrap items-center gap-1">
      {parts.length === 0 && <span className="text-slate-300">—</span>}
      {parts.map((p, i) =>
        p.full ? (
          <span key={i} className="inline-flex items-center gap-1">
            <Avatar name={p.full} />
            <span className="whitespace-nowrap text-[11px] text-ink">{p.full}</span>
          </span>
        ) : (
          <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">
            {p.token}
          </span>
        ),
      )}
    </span>
  )
}

const REL_TONE: Record<string, string> = {
  major: "bg-header text-white",
  minor: "bg-headerSub text-white",
  hotfix: "bg-priority-p1 text-white",
}

/* ------------------------------------------------------------------ view */

export function GoLive() {
  const [roster, setRoster] = useState<Person[]>([])
  const [session, setSession] = useState<Session>({ authRequired: true, user: null })
  const [list, setList] = useState<DeploymentSummary[]>([])
  const [dep, setDep] = useState<Deployment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flashId, setFlashId] = useState<string | null>(null)
  const [audit, setAudit] = useState<AuditRow[] | null>(null)
  const dragRef = useRef<{ phaseId: string; id: string } | null>(null)

  const [loginOpen, setLoginOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [completeKind, setCompleteKind] = useState<"deployment" | "rollback" | null>(null)
  const [itemModal, setItemModal] = useState<{ phase: GoLivePhase; task: GoLiveTask | null } | null>(null)

  const people = useMemo(() => roster.map((r) => r.name), [roster])
  const firstNameToFull = useMemo(() => {
    const m = new Map<string, string>()
    for (const n of people) {
      const f = n.split(/\s+/)[0]?.toLowerCase()
      if (f) m.set(f, n)
    }
    return m
  }, [people])

  // In open mode (authRequired=false) everyone edits; otherwise only a
  // signed-in user. Locked (completed) deployments are read-only regardless.
  const isEditor = !session.authRequired || !!session.user
  const canEdit = isEditor && !!dep && !dep.locked

  const loadList = useCallback(async (selectId?: number) => {
    const items = await golive.list()
    setList(items)
    const pick = selectId ?? dep?.id ?? items[0]?.id
    if (pick != null) {
      const full = await golive.get(pick)
      setDep(full)
    } else {
      setDep(null)
    }
  }, [dep?.id])

  useEffect(() => {
    ;(async () => {
      try {
        const [s, items, ppl] = await Promise.all([golive.whoami(), golive.list(), golive.people()])
        setSession(s)
        setList(items)
        setRoster(ppl)
        if (items[0]) setDep(await golive.get(items[0].id))
      } catch (e) {
        setError((e as Error).message)
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const applyUpdated = (d: Deployment) => {
    setDep(d)
    setList((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x)))
  }

  const guard = async (fn: () => Promise<Deployment>) => {
    try {
      applyUpdated(await fn())
    } catch (e) {
      const err = e as Error & { status?: number }
      if (err.status === 401) {
        setSession((s) => ({ ...s, user: null }))
        setLoginOpen(true)
      } else {
        setError(err.message)
      }
    }
  }

  const selectDeployment = async (id: number) => {
    setError(null)
    setAudit(null)
    setDep(await golive.get(id))
  }

  const flashTask = (id: string | number) => {
    const key = String(id)
    setFlashId(key)
    setTimeout(() => document.getElementById(`gl-${key}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 20)
    setTimeout(() => setFlashId((f) => (f === key ? null : f)), 1600)
  }

  const onDrop = (phase: GoLivePhase, overId: string) => {
    const drag = dragRef.current
    dragRef.current = null
    if (!drag || drag.phaseId !== phase.id || drag.id === overId || !dep) return
    const ids = phase.tasks.map((t) => String(t.id))
    const from = ids.indexOf(drag.id)
    const to = ids.indexOf(overId)
    if (from < 0 || to < 0) return
    ids.splice(to, 0, ids.splice(from, 1)[0])
    guard(() => golive.reorder(dep.id, phase.id, ids))
  }

  if (loading) return <p className="p-6 text-center text-sm text-slate-500">Loading deployments…</p>

  const statusMap = new Map<string, StatusId>()
  dep?.doc.phases.forEach((p) => p.tasks.forEach((t) => statusMap.set(String(t.id), t.status)))

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-bold text-ink">Go-Live Deployments</h2>
          {list.length > 0 && (
            <select
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-ink outline-none focus:border-headerSub"
              value={dep?.id ?? ""}
              onChange={(e) => selectDeployment(Number(e.target.value))}
            >
              {list.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.version} · {d.rel_type} · {d.status}{d.locked ? " 🔒" : ""} ({d.progress.done}/{d.progress.total})
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {session.user ? (
            <>
              <span className="text-xs text-slate-500">✎ {session.user}</span>
              <button className="rounded-lg px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100" onClick={async () => { await golive.logout(); setSession((s) => ({ ...s, user: null })) }}>
                Sign out
              </button>
            </>
          ) : session.authRequired ? (
            <button className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold text-ink hover:bg-slate-200" onClick={() => setLoginOpen(true)}>
              Sign in to edit
            </button>
          ) : (
            <button className="rounded-lg px-2.5 py-1 text-xs text-slate-400 hover:bg-slate-100" onClick={() => setLoginOpen(true)}>
              sign in (optional)
            </button>
          )}
          <button
            className="rounded-lg bg-header px-3 py-1 text-xs font-semibold text-white hover:bg-ink disabled:opacity-40"
            onClick={() => (isEditor ? setCreateOpen(true) : setLoginOpen(true))}
          >
            + New deployment
          </button>
          {dep && isEditor && (
            <button
              className="rounded-lg px-2.5 py-1 text-xs text-red-500 hover:bg-red-50"
              onClick={async () => {
                if (confirm(`Delete deployment ${dep.version} and its history? This cannot be undone.`)) {
                  await golive.remove(dep.id)
                  const items = await golive.list()
                  setList(items)
                  setDep(items[0] ? await golive.get(items[0].id) : null)
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-200">{error}</p>}

      {!dep ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
          <p className="text-sm text-slate-500">No deployments yet.</p>
          <button className="mt-3 rounded-lg bg-header px-4 py-1.5 text-sm font-semibold text-white hover:bg-ink" onClick={() => (isEditor ? setCreateOpen(true) : setLoginOpen(true))}>
            Create the first deployment
          </button>
        </div>
      ) : (
        <DeploymentView
          dep={dep}
          canEdit={canEdit}
          isEditor={isEditor}
          people={people}
          firstNameToFull={firstNameToFull}
          statusMap={statusMap}
          flashId={flashId}
          onFlash={flashTask}
          onStatus={(id, status) => guard(() => golive.patchItem(dep.id, id, { status }))}
          onDate={(id, targetDate) => guard(() => golive.patchItem(dep.id, id, { targetDate }))}
          onTime={(id, targetTime) => guard(() => golive.patchItem(dep.id, id, { targetTime }))}
          onEdit={(phase, task) => setItemModal({ phase, task })}
          onAdd={(phase) => setItemModal({ phase, task: null })}
          onDelete={(id) => {
            if (confirm(`Delete task #${id}? Any dependencies on it are cleaned up.`)) guard(() => golive.deleteItem(dep.id, id))
          }}
          onHeader={(key, value) => guard(() => golive.patchHeader(dep.id, key, value))}
          onDragStart={(phaseId, id) => (dragRef.current = { phaseId, id })}
          onDrop={onDrop}
          onComplete={(kind) => setCompleteKind(kind)}
          onReopen={() => guard(() => golive.reopen(dep.id))}
          audit={audit}
          onToggleAudit={async () => setAudit(audit ? null : await golive.audit(dep.id))}
        />
      )}

      {/* Dialogs */}
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} onLoggedIn={(name) => { setSession((s) => ({ ...s, user: name })); setLoginOpen(false) }} />
      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(d) => { setCreateOpen(false); loadList(d.id) }} />
      {dep && itemModal && (
        <ItemDialog
          open
          onClose={() => setItemModal(null)}
          deployment={dep}
          phase={itemModal.phase}
          task={itemModal.task}
          people={people}
          onSaved={(d) => { applyUpdated(d); setItemModal(null) }}
        />
      )}
      {dep && (
        <CompleteDialog open={completeKind !== null} kind={completeKind ?? "deployment"} onClose={() => setCompleteKind(null)} deployment={dep} onCompleted={(d) => { applyUpdated(d); setCompleteKind(null) }} />
      )}
    </div>
  )
}

/* ------------------------------------------------------- deployment view */

function DeploymentView({
  dep, canEdit, isEditor, people, firstNameToFull, statusMap, flashId,
  onFlash, onStatus, onDate, onTime, onEdit, onAdd, onDelete, onHeader,
  onDragStart, onDrop, onComplete, onReopen, audit, onToggleAudit,
}: {
  dep: Deployment
  canEdit: boolean
  isEditor: boolean
  people: string[]
  firstNameToFull: Map<string, string>
  statusMap: Map<string, StatusId>
  flashId: string | null
  onFlash: (id: string | number) => void
  onStatus: (id: string | number, status: StatusId) => void
  onDate: (id: string | number, date: string) => void
  onTime: (id: string | number, time: string) => void
  onEdit: (phase: GoLivePhase, task: GoLiveTask) => void
  onAdd: (phase: GoLivePhase) => void
  onDelete: (id: string | number) => void
  onHeader: (key: string, value: string) => void
  onDragStart: (phaseId: string, id: string) => void
  onDrop: (phase: GoLivePhase, overId: string) => void
  onComplete: (kind: "deployment" | "rollback") => void
  onReopen: () => void
  audit: AuditRow[] | null
  onToggleAudit: () => void
}) {
  const doc = dep.doc
  return (
    <div className="space-y-4">
      {/* Release banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-headerSub/[0.06] px-4 py-2.5 ring-1 ring-slate-200">
        <div className="flex items-center gap-2">
          <span className={clsx("rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", REL_TONE[dep.rel_type])}>{dep.rel_type}</span>
          <span className="font-display text-base font-bold text-ink">{dep.version}</span>
          <span className="num text-xs text-slate-500">{dep.progress.done}/{dep.progress.total} done · {dep.progress.pct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-[11px] text-slate-400 underline hover:text-slate-600" onClick={onToggleAudit}>
            {audit ? "hide history" : "history"}
          </button>
          {dep.locked ? (
            <>
              <span
                className={clsx(
                  "rounded px-2 py-0.5 text-[10px] font-bold uppercase",
                  dep.outcome === "rolled_back" ? "bg-red-100 text-red-700" : "bg-win-light text-win-deep",
                )}
              >
                {dep.outcome === "rolled_back" ? "Rolled back" : "Completed"} · locked
              </span>
              {isEditor && (
                <button className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-header ring-1 ring-slate-300 hover:bg-slate-50" onClick={onReopen}>
                  Reopen
                </button>
              )}
            </>
          ) : (
            canEdit && (
              <>
                <button className="rounded-lg bg-win px-3 py-1 text-xs font-semibold text-white hover:bg-win-deep" onClick={() => onComplete("deployment")}>
                  Deployment &amp; post-deployment completed
                </button>
                <button className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-priority-p0 ring-1 ring-priority-p0/40 hover:bg-red-50" onClick={() => onComplete("rollback")}>
                  Rollback completed
                </button>
              </>
            )
          )}
        </div>
      </div>

      {dep.locked && dep.completion_notes && (
        <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600 ring-1 ring-slate-200">
          <span className="font-semibold text-ink">Completion notes</span>
          <p className="mt-1 whitespace-pre-wrap">{dep.completion_notes}</p>
        </div>
      )}

      {audit && (
        <div className="max-h-48 overflow-y-auto rounded-xl bg-white p-3 text-xs ring-1 ring-slate-200">
          {audit.length === 0 ? (
            <p className="text-slate-400">No history yet.</p>
          ) : (
            <ul className="space-y-0.5">
              {audit.map((a, i) => (
                <li key={i} className="flex gap-2 text-slate-600">
                  <span className="num w-32 shrink-0 text-slate-400">{a.at.replace("T", " ").slice(0, 16)}</span>
                  <span className="font-semibold text-ink">{a.actor}</span>
                  <span>{a.action}</span>
                  {a.detail?.id != null && <span className="num text-slate-400">#{String(a.detail.id)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Header fields */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200/80 bg-slate-200/60 sm:grid-cols-3 lg:grid-cols-4">
        {doc.header.map((f) => (
          <div key={f.key} className="bg-white px-3 py-2">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.06em] text-slate-400">{f.label}</div>
            {canEdit ? (
              <HeaderControl field={f} depId={dep.id} people={people} onHeader={onHeader} />
            ) : (
              <div className="mt-0.5 text-[13px] font-medium text-ink">{f.value || <span className="text-slate-300">—</span>}</div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
        <span className="font-bold uppercase tracking-wider text-slate-400">Status</span>
        {STATUS_IDS.map((s) => <StatusChip key={s} status={s} />)}
        {canEdit && <span className="ml-1 text-slate-400">· click status/date/time to edit, drag rows to reorder, click a dependency to jump</span>}
      </div>

      {/* Phases */}
      {doc.phases.map((phase) => (
        <PhaseTable
          key={phase.id}
          phase={phase}
          canEdit={canEdit}
          firstNameToFull={firstNameToFull}
          statusMap={statusMap}
          flashId={flashId}
          onFlash={onFlash}
          onStatus={onStatus}
          onDate={onDate}
          onTime={onTime}
          onEdit={onEdit}
          onAdd={onAdd}
          onDelete={onDelete}
          onDragStart={onDragStart}
          onDrop={onDrop}
        />
      ))}
    </div>
  )
}

/* --------------------------------------------------------- header control */

const HDR_SELECT =
  "mt-0.5 w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-[13px] font-medium text-ink outline-none focus:border-headerSub"

function HeaderControl({
  field,
  depId,
  people,
  onHeader,
}: {
  field: GoLiveHeaderField
  depId: number
  people: string[]
  onHeader: (key: string, value: string) => void
}) {
  if (field.key === "projectName") {
    return (
      <select className={HDR_SELECT} value={field.value || "CAT-OS"} onChange={(e) => onHeader(field.key, e.target.value)}>
        <option value="CAT-OS">CAT-OS</option>
      </select>
    )
  }
  if (field.key === "deployPattern") {
    return (
      <select className={HDR_SELECT} value={field.value} onChange={(e) => onHeader(field.key, e.target.value)}>
        {PATTERN_OPTS.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    )
  }
  if (field.key in LEAD_MAX) {
    return <LeadPicker value={field.value} people={people} max={LEAD_MAX[field.key]} onChange={(v) => onHeader(field.key, v)} />
  }
  return (
    <input
      key={`${depId}-${field.key}-${field.value}`}
      type={field.type ?? "text"}
      defaultValue={field.value}
      placeholder={field.placeholder}
      onBlur={(e) => e.target.value !== field.value && onHeader(field.key, e.target.value)}
      className="mt-0.5 w-full border-b border-transparent bg-transparent text-[13px] font-medium text-ink outline-none placeholder:text-slate-300 focus:border-headerSub"
    />
  )
}

/** Up to `max` person dropdowns; joins the picks as "A / B". */
function LeadPicker({
  value,
  people,
  max,
  onChange,
}: {
  value: string
  people: string[]
  max: number
  onChange: (v: string) => void
}) {
  const current = value.split("/").map((s) => s.trim()).filter(Boolean)
  const slots = Array.from({ length: max }, (_, i) => current[i] ?? "")
  const setSlot = (i: number, name: string) => {
    const next = [...slots]
    next[i] = name
    onChange(next.filter(Boolean).join(" / "))
  }
  return (
    <div className="mt-0.5 space-y-1">
      {slots.map((slot, i) => (
        <select key={i} className={HDR_SELECT} value={slot} onChange={(e) => setSlot(i, e.target.value)}>
          <option value="">{max > 1 ? `— lead ${i + 1} —` : "—"}</option>
          {people.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------ phase table */

function PhaseTable({
  phase, canEdit, firstNameToFull, statusMap, flashId,
  onFlash, onStatus, onDate, onTime, onEdit, onAdd, onDelete, onDragStart, onDrop,
}: {
  phase: GoLivePhase
  canEdit: boolean
  firstNameToFull: Map<string, string>
  statusMap: Map<string, StatusId>
  flashId: string | null
  onFlash: (id: string | number) => void
  onStatus: (id: string | number, status: StatusId) => void
  onDate: (id: string | number, date: string) => void
  onTime: (id: string | number, time: string) => void
  onEdit: (phase: GoLivePhase, task: GoLiveTask) => void
  onAdd: (phase: GoLivePhase) => void
  onDelete: (id: string | number) => void
  onDragStart: (phaseId: string, id: string) => void
  onDrop: (phase: GoLivePhase, overId: string) => void
}) {
  // During-Deployment tasks are time-driven (from the maintenance window), not
  // dated; every other phase uses a date.
  const isDuring = phase.num === "2" && !phase.isRollback
  const done = phase.tasks.filter((t) => t.status === "done" || t.status === "na").length
  return (
    <details open className="rounded-xl border border-slate-200/80">
      <summary className={clsx("flex cursor-pointer flex-wrap items-center gap-3 rounded-t-xl border-b-2 px-3 py-2.5 marker:content-none", phase.isRollback ? "border-b-priority-p0 bg-red-50/70" : "border-b-header bg-headerSub/[0.08]")}>
        <span className={clsx("grid h-6 w-6 shrink-0 place-items-center rounded text-[13px] font-bold text-white", phase.isRollback ? "bg-priority-p0" : "bg-header")}>{phase.num}</span>
        <span className="min-w-[160px] flex-1">
          <span className={clsx("font-display text-sm font-bold", phase.isRollback ? "text-priority-p0" : "text-header")}>{phase.title}</span>
          {phase.subtitle && <span className="ml-2 text-[11px] font-normal text-slate-500">{phase.subtitle}</span>}
        </span>
        <span className="num text-[11px] font-semibold text-slate-500">{done}/{phase.tasks.length}</span>
      </summary>

      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              {canEdit && <th className="w-6"></th>}
              <th className="w-10 !text-right">#</th>
              <th>Status</th>
              <th>Task</th>
              <th>Assigned To</th>
              <th>Target</th>
              <th>Depends On</th>
              <th>Comments</th>
              {canEdit && <th className="w-16"></th>}
            </tr>
          </thead>
          <tbody>
            {phase.tasks.map((t) => {
              const flashed = flashId === String(t.id)
              return (
                <tr
                  key={String(t.id)}
                  id={`gl-${t.id}`}
                  className={clsx("align-top transition-colors", flashed && "bg-warn-light/70 ring-2 ring-warn/50")}
                  draggable={canEdit}
                  onDragStart={() => canEdit && onDragStart(phase.id, String(t.id))}
                  onDragOver={(e) => canEdit && e.preventDefault()}
                  onDrop={() => canEdit && onDrop(phase, String(t.id))}
                >
                  {canEdit && <td className="cursor-grab text-slate-300" title="Drag to reorder">⠿</td>}
                  <td className="num !text-right text-slate-400">{t.id}</td>
                  <td>
                    {canEdit ? (
                      <select
                        value={t.status}
                        onChange={(e) => onStatus(t.id, e.target.value as StatusId)}
                        className={clsx("rounded-full border-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 outline-none", TONE[STATUS_META[t.status].tone])}
                      >
                        {STATUS_IDS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                      </select>
                    ) : (
                      <StatusChip status={t.status} />
                    )}
                  </td>
                  <td className="min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-ink">{t.title}</span>
                      <TaskFlags task={t} />
                    </div>
                    {t.ref && <div className="mt-0.5 font-mono text-[9.5px] text-slate-400">{t.ref}</div>}
                    {t.updatedBy && <div className="mt-0.5 text-[9px] text-slate-300">✎ {t.updatedBy} · {(t.updatedAt ?? "").replace("T", " ").slice(0, 16)}</div>}
                  </td>
                  <td><OwnerCell task={t} firstNameToFull={firstNameToFull} /></td>
                  <td className="whitespace-nowrap text-[11px] text-slate-500">
                    {canEdit ? (
                      isDuring ? (
                        <input type="time" value={t.targetTime ?? ""} onChange={(e) => onTime(t.id, e.target.value)} className="rounded border border-slate-200 px-1 py-0.5 text-[11px] text-slate-600 outline-none focus:border-headerSub" title="From the maintenance window; editable" />
                      ) : (
                        <input type="date" value={t.targetDate ?? ""} onChange={(e) => onDate(t.id, e.target.value)} className="rounded border border-slate-200 px-1 py-0.5 text-[11px] text-slate-600 outline-none focus:border-headerSub" />
                      )
                    ) : isDuring ? (
                      t.targetTime ? <span className="num">{t.targetTime}</span> : <span className="text-slate-300">—</span>
                    ) : t.targetDate ? (
                      <>{t.targetDate}{t.targetTime && <span className="num"> {t.targetTime}</span>}</>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td>
                    {t.dependsOn.length ? (
                      <span className="flex flex-wrap gap-0.5">
                        {t.dependsOn.map((d) => {
                          const st = statusMap.get(String(d))
                          const unmet = st !== "done" && st !== "na"
                          return (
                            <button
                              key={String(d)}
                              onClick={() => onFlash(d)}
                              title={unmet ? "Dependency not yet done — click to jump" : "Click to jump"}
                              className={clsx("num rounded px-1 text-[10px] ring-1 transition hover:brightness-95", unmet ? "bg-red-50 text-red-700 ring-red-200" : "bg-win-light text-win-deep ring-win/30")}
                            >
                              {d}
                            </button>
                          )
                        })}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="min-w-[200px] max-w-[340px] text-[11px] leading-snug text-slate-600">{t.comments || <span className="text-slate-300">—</span>}</td>
                  {canEdit && (
                    <td className="whitespace-nowrap">
                      <button className="text-[11px] text-headerSub hover:underline" onClick={() => onEdit(phase, t)}>Edit</button>
                      <button className="ml-2 text-[11px] text-red-500 hover:underline" onClick={() => onDelete(t.id)}>Del</button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <button className="w-full rounded-b-xl border-t border-slate-100 px-3 py-1.5 text-left text-[11px] font-semibold text-headerSub hover:bg-headerSub/[0.05]" onClick={() => onAdd(phase)}>
          + Add task to {phase.title}
        </button>
      )}
    </details>
  )
}
